import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { getCurrentNote, removeCurrentNote } from 'modules/networkTools/localStorageTokens';
import {
  deleteNoteInput,
  getClientNote,
  putUpdateNote
} from 'store/clientProfile/clientProfileActions';
import { removeNote } from 'store/clientProfile/clientProfileSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getTodos as getMyTodos } from 'store/myTodos/myTodosActions';
import { getTodos as getRoadmapTodos } from 'store/roadmaps/roadmapsActions';
import { notifyUserSuccess } from 'utils/notifications';

import { useNoteFormContext } from '../context/noteFormContext';
import { useAutosaveContext } from '../context/autosaveContext';
import { NoteFormValues } from '../utils/validation';
import { createDefaultValues, remapValues, removeEmptyFieldsFromObject } from '../utils/helpers';

type UseNoteFormActionsOptions = {
  onSubmitSuccess?: () => void;
  onDeleteSuccess?: () => void;
  isPopup?: boolean;
};

export const useNoteFormActions = (options: UseNoteFormActionsOptions = {}) => {
  const { onSubmitSuccess, onDeleteSuccess, isPopup = false } = options;
  const dispatch = useAppDispatch();
  const { clientId, note } = useNoteFormContext();
  // Fall back to localStorage if note.id is not available (e.g., after autosave creates a new note)
  const noteId = note?.id?.toString() ?? getCurrentNote()?.currentNoteID?.toString();
  const { clearLastSaved, isAutosaving } = useAutosaveContext();
  const formik = useFormikContext<NoteFormValues>();
  const sitesEnabled = useAppSelector((state) => state.clients.client.data?.enableProgramSites);
  const isBillingEnabled = useAppSelector((state) => state.clients.client.data?.enableBilling);

  const handleSubmit = useCallback(async () => {
    if (!noteId) return;

    const dataUpdate = remapValues({
      values: formik.values,
      clientId,
      id: noteId,
      draft: false
    });
    const res = await dispatch(putUpdateNote(removeEmptyFieldsFromObject(dataUpdate) as any));

    if (res.meta.requestStatus === 'fulfilled') {
      const channel = new BroadcastChannel('note-update');
      channel.postMessage({ message: 'EDIT_NOTE', data: dataUpdate });

      if (formik.values.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
        channel.postMessage({ message: 'SAFETY_NOTE' });
      }

      if (formik.values.nextContact != null && formik.values.nextContact !== '') {
        notifyUserSuccess('Next contact to-do created');
        if (isPopup) {
          channel.postMessage({ message: 'NEXT_CONTACT_TODO' });
        } else {
          dispatch(getMyTodos());
          dispatch(getRoadmapTodos({ clientId }));
        }
      }

      channel.close();
      removeCurrentNote();
      clearLastSaved();
      notifyUserSuccess('Note saved.');

      formik.resetForm({
        values: createDefaultValues(sitesEnabled, isBillingEnabled)
      });
      dispatch(removeNote());

      if (isPopup) {
        await dispatch(
          getClientNote({
            clientId,
            noteId
          })
        );
      }

      onSubmitSuccess?.();
    }
  }, [
    noteId,
    formik,
    clientId,
    note,
    isPopup,
    dispatch,
    clearLastSaved,
    sitesEnabled,
    isBillingEnabled,
    onSubmitSuccess
  ]);

  const handleDelete = useCallback(async () => {
    formik.resetForm({
      values: createDefaultValues(sitesEnabled, isBillingEnabled)
    });
    clearLastSaved();
    dispatch(removeNote());

    if (noteId) {
      await dispatch(
        deleteNoteInput({
          clientId,
          noteId
        })
      );

      removeCurrentNote();
      const channel = new BroadcastChannel('note-update');
      channel.postMessage({ message: 'DELETE_NOTE', noteId });
      if (note?.noteType === 'SAFETY_NOTE') {
        channel.postMessage({ message: 'SAFETY_NOTE' });
      }
      channel.close();

      onDeleteSuccess?.();

      if (isPopup) {
        window.close();
      }
    }
  }, [
    formik,
    sitesEnabled,
    isBillingEnabled,
    clearLastSaved,
    dispatch,
    noteId,
    clientId,
    note,
    onDeleteSuccess,
    isPopup
  ]);

  const handleReset = useCallback(() => {
    formik.resetForm({
      values: createDefaultValues(sitesEnabled, isBillingEnabled)
    });
    removeCurrentNote();
    dispatch(removeNote());
    clearLastSaved();
  }, [formik, sitesEnabled, isBillingEnabled, dispatch, clearLastSaved]);

  return {
    handleSubmit,
    handleDelete,
    handleReset,
    isSubmitting: formik.isSubmitting,
    isValid: formik.isValid,
    isAutosaving
  };
};
